import React, { useState } from "react";
import Panel from "@adsk/alloy-react-panel";
import {
  IssueDetailPanel,
  useIssueDetailPanelTitle,
} from "../../IssueDetailPanel";
import { IssueFilterPanelBody } from "./IssueFilterPanelBody";
import { IssueExportPanelBody } from "@adsk/acc-issues-common/components/form/IssueExportPanelBody";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getNumberOfIssueTableSelectedIds } from "@adsk/acc-issues-core/state/redux/ducks/issueTableSelection";
import { CenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import { useTranslations } from "@adsk/acc-issues-translations";
import { HydratedBulkEditPanel } from "./HydratedBulkEditPanel";
import { PanelType } from "./PanelType";
import { TabTitle } from "@adsk/exo-skeleton-react";

import {
  useClearAllFilters,
  useIsFilteredApplied,
  useIsResetDisabled,
  useResetFilters,
} from "@adsk/acc-issues-core/state/issuesFetchParams/useIssueFetchParams";
import { LinkButton } from "@adsk/alloy-react-button";
import styled from "styled-components";
import Tooltip from "@adsk/alloy-react-tooltip";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { IssueDetailPanelConfigProvider } from "../../IssueDetailPanel/issueDetailPanelConfigContext";

const FilterActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 10px;
`;

const usePanelTitle = (panelType: PanelType, issueId?: string) => {
  const issueDetailPanelTitle = useIssueDetailPanelTitle(issueId);
  const numberOfIssueTableSelectedIds = useSelector(
    getNumberOfIssueTableSelectedIds
  );
  const { T } = useTranslations();
  switch (panelType) {
    case PanelType.IssueDetailPanel:
      return issueDetailPanelTitle;
    case PanelType.FilterPanel:
      return T.translate("filterPanel.title");
    case PanelType.BulkEditPanel:
      return T.translate("issueBulkEdit.panelTitle", {
        selectedCount: numberOfIssueTableSelectedIds,
      });
    case PanelType.None:
    default:
      return "";
  }
};

export const IssueLogPanel = React.memo(
  ({
    panelType,
    issueId,
    onClose,
    onRestoreSuccess,
  }: {
    panelType: PanelType;
    issueId?: string;
    onClose: () => void;
    onRestoreSuccess: () => void;
  }) => {
    const { T } = useTranslations();
    const title = usePanelTitle(panelType, issueId);
    const [shouldHidePanelContent, setShouldHidePanelContent] = useState(
      !issueId
    );

    const clearAllFilters = useClearAllFilters();
    const isResetDisabled = useIsResetDisabled();
    const isFilteredApplied = useIsFilteredApplied();
    const resetFilters = useResetFilters();

    const { value: isInitialFilterEnabled } = useFeatureFlag(
      FeatureFlag.UnresolvedIssuesInitialFilter
    );

    const renderHeader = () => {
      if (panelType === PanelType.ExportPanel) {
        return <></>;
      }

      const renderActions = () => {
        if (!isInitialFilterEnabled || panelType !== PanelType.FilterPanel) {
          return null;
        }

        return (
          <FilterActions>
            <Tooltip content={T.translate("actions.resetFilters")}>
              <LinkButton
                data-testid="reset-filters-button"
                onClick={resetFilters}
                disabled={isResetDisabled}
              >
                {T.translate("actions.reset")}
              </LinkButton>
            </Tooltip>
            <Tooltip content={T.translate("actions.clearAllFilters")}>
              <LinkButton
                data-testid="clear-all-filters-button"
                onClick={clearAllFilters}
                disabled={!isFilteredApplied}
              >
                {T.translate("actions.clearAll")}
              </LinkButton>
            </Tooltip>
          </FilterActions>
        );
      };

      return (
        <Panel.Header
          data-testid="panel-header"
          disableDivider={panelType === PanelType.IssueDetailPanel}
          title={title}
          onClose={onClose}
          renderActions={renderActions}
        />
      );
    };

    return (
      <Panel
        data-testid="issue-panel"
        open={panelType !== PanelType.None}
        onClose={() => {
          onClose();
          setShouldHidePanelContent(true);
        }}
        onAnimationComplete={() =>
          setShouldHidePanelContent(panelType === PanelType.None)
        }
        renderHeader={renderHeader}
      >
        {
          {
            [PanelType.IssueDetailPanel]: shouldHidePanelContent ? (
              <CenteredSpinner />
            ) : (
              <div
                // 58px is the size of the header, which is passed via `renderHeader`.
                // it becomes a sibling to this component, so we need to make space for it, because
                // otherwise our flex sizing is messed up because the children of this component will
                // act as if this component is 100% instead of 100% - the header (which is shown because of flex)
                // For the other panels this doesn't matter so much, but because
                // the viewer uses the detail panel and heandles the headers differently,
                // we need to make sure to adjust it here.
                style={{ height: "calc(100% - 58px)" }}
              >
                <TabTitle value={title as string} />
                <IssueDetailPanelConfigProvider
                  config={{
                    placement: {
                      navigable: true,
                      editable: true,
                      unlinkable: true,
                    },
                  }}
                >
                  <IssueDetailPanel
                    issueId={issueId!}
                    onDelete={onClose}
                    onRestoreSuccess={onRestoreSuccess}
                  />
                </IssueDetailPanelConfigProvider>
              </div>
            ),
            [PanelType.FilterPanel]: <IssueFilterPanelBody />,
            [PanelType.BulkEditPanel]: (
              <HydratedBulkEditPanel onSave={onClose} />
            ),
            [PanelType.ExportPanel]: (
              <IssueExportPanelBody
                onClose={() => {
                  onClose();
                  setShouldHidePanelContent(true);
                }}
              />
            ),
            [PanelType.None]: null,
          }[panelType]
        }
      </Panel>
    );
  }
);
